import { api } from '@/lib/api.js';

// Client for the "Sign with Tower" engine (/api/sign/*). Templates + their
// field maps in Phase 2; documents/signing land in later phases.

export function listTemplates(status = 'active') {
  return api.get(`/api/sign/templates?status=${encodeURIComponent(status)}`);
}

export function getTemplate(id) {
  return api.get(`/api/sign/templates/${encodeURIComponent(id)}`);
}

// Create a template from an uploaded PDF. Multipart, so raw fetch + JWT (the
// shared api wrapper hardcodes JSON); the browser sets the multipart boundary,
// so we send no Content-Type header. Mirrors quotes.service attachQuoteSourcePdf.
export async function uploadTemplate(file, { name, docType } = {}) {
  const fd = new FormData();
  fd.append('file', file);
  if (name) fd.append('name', name);
  if (docType) fd.append('doc_type', docType);
  const res = await fetch(`/api/sign/templates?_t=${Date.now()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Template upload failed (HTTP ${res.status})`);
  }
  return res.json();
}

// Save the full field map for a template (builder save — replace semantics).
export function saveTemplateFields(id, fields) {
  return api.put(`/api/sign/templates/${encodeURIComponent(id)}/fields`, { fields });
}

// Fetch the source PDF bytes (gated _blob route) for in-browser rendering.
export async function fetchSourcePdf(sourceUrl) {
  const res = await fetch(sourceUrl, {
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
  });
  if (!res.ok) throw new Error(`Could not load source PDF (HTTP ${res.status})`);
  return res.arrayBuffer();
}

// ─── documents (broker) ──────────────────────────────────────────
export function createDocument(payload) {
  return api.post('/api/sign/documents', payload);
}
export function getDocument(id) {
  return api.get(`/api/sign/documents/${encodeURIComponent(id)}`);
}
export function sendDocument(id) {
  return api.post(`/api/sign/documents/${encodeURIComponent(id)}/send`, {});
}

// ─── guest signing flow (token-gated, NO JWT) ────────────────────
// These hit /api/sign/g/:token directly with no Authorization header — the URL
// token IS the credential. Errors carry the server's status so the UI can
// distinguish expired (410) / locked-out (429) / wrong-code (401).
async function guest(method, path, body) {
  const res = await fetch(`/api/sign/g/${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Error ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

export function getSigner(token) {
  return guest('GET', `${encodeURIComponent(token)}?_t=${Date.now()}`);
}
export function verifyOtp(token, code) {
  return guest('POST', `${encodeURIComponent(token)}/otp/verify`, { code });
}
export function submitSignature(token, payload) {
  return guest('POST', `${encodeURIComponent(token)}/submit`, payload);
}
export function guestPreviewUrl(token) {
  return `/api/sign/g/${encodeURIComponent(token)}/preview?_t=${Date.now()}`;
}
